import React, { useRef, useEffect, useContext, useState } from "react";
import Typed from "typed.js";
import { motion } from "framer-motion/dist/framer-motion";
import { headingVariant, containerVariant, itemVariant } from "./PageVariants";
import { NavContext } from "./App";
import { InView } from "react-intersection-observer";

import FoodOrderingSystemHome from "./assets/projects/FoodOrderingSystem/Home.png";
import FoodOrderingSystemMenu from "./assets/projects/FoodOrderingSystem/Menu.jpg";
import FoodOrderingSystemDashboard from "./assets/projects/FoodOrderingSystem/Dashboard.jpg";

import ChainReactionGameStarted from "./assets/projects/ChainReaction/GameStarted.png";
import ChainReactionMultiPlaying from "./assets/projects/ChainReaction/Multi-Playing.png";
import ChainReactionGameResult from "./assets/projects/ChainReaction/GameResult.png";

import CalendarAppModes from "./assets/projects/CalendarApp/Calendar_Light_Dark_Modes.png";
import CalendarAppEventAddUpdate from "./assets/projects/CalendarApp/Calendar_Add_Update.png";
import CalendarAppEventDelete from "./assets/projects/CalendarApp/Calendar_Event_Delete.png";

import BlazorComponentsDesktopView from "./assets/projects/BlazorComponents/DesktopView.png";
import BlazorComponentsMobileView from "./assets/projects/BlazorComponents/MobileView.png";
import BlazorComponentsTabletView from "./assets/projects/BlazorComponents/TabletView.png";

export default function Projects({ pageRef }) {
	const typedElementForSub = useRef(null);
	const typedElementForHeading = useRef(null);
	const setSelectedNav = useContext(NavContext);
	const [isInView, setIsInView] = useState(false);
	const projectList = [
		{
			name: "Food Ordering System",
			technologiesUsed: [
				"react",
				"nodejs",
				"graphql",
				"express",
				"typescript",
				"apollo-client",
				"type-orm",
				"jwt",
				"apollo-server",
				"formik",
				"tailwindcss",
				"framer-motion",
			],
			details: [
				"Completely built with react hooks and functional components",
				"Emulates online ordering, chef-counter update interface and business owner dashboard",
				"Real-time updates of food orders and availability on the website",
			],
			image1: FoodOrderingSystemHome,
			image2: FoodOrderingSystemMenu,
			image3: FoodOrderingSystemDashboard,
		},
		{
			name: "Chain Reaction",
			technologiesUsed: [
				"react",
				"express",
				"node",
				"socket-io",
				"tailwindcss",
				"framer-motion",
			],
			details: [
				"Attempted to created an old school game using react and node js",
				"Multi-player access provided with socket communication (used socket-io)",
				"Assesses the winner on every move and decides the winner",
			],
			image1: ChainReactionGameResult,
			image2: ChainReactionMultiPlaying,
			image3: ChainReactionGameStarted,
		},
		{
			name: "Calendar App",
			technologiesUsed: [
				"blazor-webassembly",
				"postcss-plugin",
				"tailwindcss",
				"font-awesome-5",
			],
			details: [
				"A regular calendar app designed and developed using Blazor Web assembly",
				"Explored DynamicComponent feature of NET 6.0 in creating custom modal dialogs",
				"Enhancement is needed as it currently available only with in-memory data and all the CRUD operation can be performed",
			],
			image1: CalendarAppModes,
			image2: CalendarAppEventAddUpdate,
			image3: CalendarAppEventDelete,
		},
		{
			name: "Blazor Components",
			technologiesUsed: ["blazor-webassembly", "vanilla javascript", "css"],
			details: [
				"Reuasble components created using blazor webassembly",
				"New feature: Dynamic Component of NET 6.0 is consumed for custom modal dialog",
				"Hosted a github page to demonstrate the usage and provided multiple options to adjust the dialog as per user's requirement",
			],
			image1: BlazorComponentsDesktopView,
			image2: BlazorComponentsTabletView,
			image3: BlazorComponentsMobileView,
		},
	];

	useEffect(() => {
		let typedSub = new Typed(typedElementForSub.current, {
			strings: ["TAKE A LOOK AT MY"],
			startDelay: 0,
			typeSpeed: 40,
			showCursor: false,
		});

		let typedHeading = new Typed(typedElementForHeading.current, {
			strings: ["PROJECTS"],
			startDelay: 800,
			typeSpeed: 80,
			showCursor: false,
		});

		return () => {
			typedSub.destroy();
			typedHeading.destroy();
		};
	}, [isInView]);

	const viewChange = (inView) => {
		setIsInView(inView);
		if (inView) {
			setSelectedNav({ Heading: "PROJECTS" });
		}
	};

	return (
		<InView threshold={0.1} onChange={(inView, entry) => viewChange(inView)}>
			<div
				ref={pageRef}
				className="w-full md:w-10/12 m-auto grid grid-cols-1 justify-start items-start"
			>
				<motion.h4
					variants={headingVariant}
					initial="hide"
					animate={isInView ? "show" : "hide"}
					exit="hide"
					className="text-3xl text-green-600 border-l-8 border-green-600 pl-4 pt-6 h-24 tracking-wider"
				>
					<span
						className="text-gray-600 dark:text-gray-400 text-sm"
						ref={typedElementForSub}
					></span>
					<br />
					<span className="text-green-600" ref={typedElementForHeading}></span>
				</motion.h4>
				<motion.div
					variants={containerVariant}
					initial="hide"
					animate="show"
					className="text-gray-500 my-8 rounded-3xl text-xs flex flex-wrap space-y-10 dark:text-gray-200"
				>
					{projectList &&
						projectList.map((project, idx) => (
							<motion.div
								key={idx}
								variants={itemVariant}
								className="w-full h-full p-1 md:p-4 lg:p-10 my-8 lg:h-400 shadow-lg grid grid-cols-1 grid-rows-none lg:grid-cols-6 lg:grid-rows-2 text-lg gap-4 rounded-3xl bg-white dark:bg-gray-600"
							>
								<div className="rounded-lg p-3 md:p-0 lg:col-span-3 lg:row-span-2">
									<h3 className="text-xl pb-2 uppercase font-semibold text-green-500">
										{project.name}
									</h3>
									<p className="text-base font-semibold">Technologies: </p>
									<p className="flex flex-wrap">
										{project.technologiesUsed &&
											project.technologiesUsed.length > 0 &&
											project.technologiesUsed.map((singleTech, idx) => (
												<span
													key={singleTech}
													className="px-2 py-0.5 m-0.5 whitespace-nowrap rounded-full bg-green-600 text-gray-200 text-xs"
												>
													{singleTech}
												</span>
											))}
									</p>
									<br />
									<p className="text-base font-semibold">Details: </p>
									<ul className="m-2 pl-4 text-sm space-y-1 list-disc">
										{project.details.map((projectDetail, idx) => (
											<li key={idx}>{projectDetail}</li>
										))}
									</ul>
								</div>
								{idx % 2 === 0 && (
									<>
										<img
											className="cursor-pointer border border-gray-300 dark:border-gray-600 hover:shadow-xl m-3 md:m-0 rounded-lg w-11/12 md:w-full h-400 md:h-full lg:col-start-4 lg:row-start-1 lg:col-span-2 lg:row-span-2 object-cover object-left-top"
											src={project.image1}
											alt={project.name + " Image"}
										/>
										<img
											className="cursor-pointer border border-gray-300 dark:border-gray-600 hover:shadow-xl rounded-lg w-full h-full hidden lg:block lg:col-start-6 lg:row-start-1 object-cover object-left-top"
											src={project.image2}
											alt={project.name + " Image"}
										/>
										<img
											className="cursor-pointer border border-gray-300 dark:border-gray-600 hover:shadow-xl rounded-lg w-full h-full hidden lg:block lg:col-start-6 lg:row-start-2 object-cover object-left-top"
											src={project.image3}
											alt={project.name + " Image"}
										/>
									</>
								)}
								{idx % 2 === 1 && (
									<>
										<img
											className="cursor-pointer border border-gray-300 dark:border-gray-600 hover:shadow-xl rounded-lg w-full h-full hidden lg:block lg:col-start-4 lg:row-start-1 object-cover object-left-top"
											src={project.image1}
											alt={project.name + " Image"}
										/>
										<img
											className="cursor-pointer border border-gray-300 dark:border-gray-600 hover:shadow-xl rounded-lg w-full h-full hidden lg:block lg:col-start-4 lg:row-start-2 object-cover object-left-top"
											src={project.image3}
											alt={project.name + " Image"}
										/>
										<img
											className="cursor-pointer border border-gray-300 dark:border-gray-600 hover:shadow-xl m-3 md:m-0 rounded-lg w-11/12 md:w-full h-400 md:h-full lg:col-start-5 lg:row-start-1 lg:col-span-2 lg:row-span-2 object-cover object-left-top"
											src={project.image2}
											alt={project.name + " Image"}
										/>
									</>
								)}
							</motion.div>
						))}
				</motion.div>
			</div>
		</InView>
	);
}
